import { match, P } from "ts-pattern";

import type { Database } from "@/db/schema";
import type { Gender } from "@/db/schema/shared";

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

export const MIN_SANCTION_TEAMS = 8;
export const MIN_JUNIORS_SANCTION_TEAMS = 5;
export const MAGIC_RESANCTION_TEAMS = 12;

// ---------------------------------------------------------------------------
// Reward tables — ported from the legacy Rust code in tournament_finish.rs
// ---------------------------------------------------------------------------

export type RatingName = "aaa" | "aa" | "a" | "b" | null;

export interface RatedRewards {
  rating: string;
  minRated: number;
  autoSanction: number | null;
  points: [number, number, number, number, number];
  ratings: (
    | [number, [RatingName, RatingName, RatingName, RatingName]]
    | null
  )[];
}

// prettier-ignore
export const MENS_REWARDS: RatedRewards[] = [
  // AAA / Open
  {
    rating: "aaa",
    minRated: 8,
    autoSanction: null,
    points: [2000, 1600, 1400, 1000, 600],
    ratings: [
      [30, ["aaa", "aaa", "aaa", "aa"]],
      [20, ["aaa", "aaa", "aa", "a"]],
      [12, ["aaa", "aaa", "aa", null]],
    ],
  },
  // AA
  {
    rating: "aa",
    minRated: 8,
    autoSanction: null,
    points: [900, 720, 630, 450, 270],
    ratings: [
      [30, ["aaa", "aa", "aa", "a"]],
      [20, ["aaa", "aa", "aa", "b"]],
      [12, ["aaa", "aa", "a", null]],
    ],
  },
  // A
  {
    rating: "a",
    minRated: 8,
    autoSanction: null,
    points: [405, 324, 283.5, 202.5, 0],
    ratings: [[12, ["aa", "a", "a", "b"]], null, null],
  },
  // B
  {
    rating: "b",
    minRated: 8,
    autoSanction: 16,
    points: [182, 145.6, 127.4, 0, 0],
    ratings: [
      [30, ["a", "b", "b", "b"]],
      [20, ["a", "b", "b", null]],
      [8, ["a", "b", null, null]],
    ],
  },
  // Unrated
  {
    rating: "unrated",
    minRated: 0,
    autoSanction: null,
    points: [100, 80, 0, 0, 0],
    ratings: [[20, ["b", "b", null, null]], [8, ["b", null, null, null]], null],
  },
];

// prettier-ignore
export const WOMENS_REWARDS: RatedRewards[] = [
  // AAA / Open
  {
    rating: "aaa",
    minRated: 8,
    autoSanction: null,
    points: [2000, 1600, 1400, 1000, 600],
    ratings: [
      [30, ["aaa", "aaa", "aaa", "aa"]],
      [20, ["aaa", "aaa", "aa", "a"]],
      [12, ["aaa", "aaa", "aa", null]],
    ],
  },
  // AA
  {
    rating: "aa",
    minRated: 8,
    autoSanction: null,
    points: [900, 720, 630, 450, 270],
    ratings: [
      [20, ["aaa", "aa", "aa", "a"]],
      [12, ["aaa", "aa", "a", null]],
      null,
    ],
  },
  // A
  {
    rating: "a",
    minRated: 4,
    autoSanction: null,
    points: [405, 324, 283.5, 202.5, 0],
    ratings: [[12, ["aa", "a", "a", "b"]], [8, ["aa", "a", "a", null]], null],
  },
  // B
  {
    rating: "b",
    minRated: 2,
    autoSanction: 8,
    points: [182, 145.6, 127.4, 0, 0],
    ratings: [
      [20, ["a", "b", "b", "b"]],
      [13, ["a", "b", "b", null]],
      [4, ["a", "b", null, null]],
    ],
  },
  // Unrated
  {
    rating: "unrated",
    minRated: 0,
    autoSanction: null,
    points: [100, 80, 0, 0, 0],
    ratings: [[4, ["b", null, null, null]], null, null],
  },
];

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

export function divisionToRewardIndex(divisionName: string): number | null {
  return match(divisionName)
    .with(P.union("open", "aaa"), () => 0)
    .with("aa", () => 1)
    .with("a", () => 2)
    .with("b", () => 3)
    .with("unrated", () => 4)
    .otherwise(() => null);
}

export function lowerRewardIndex(index: number): number | null {
  if (index < 4) return index + 1;
  return null;
}

/**
 * Determine the rewards (points array + ratings array) for a division,
 * with sanction cascade if not enough rated players.
 *
 * Returns the settled tier's rating name alongside points/ratings so
 * callers can detect when a division was downgraded.
 */
export function getRatedRewards(
  rewardTable: RatedRewards[],
  rewardIndex: number,
  numTeams: number,
  countRatings: (levelOrder: number) => number,
  levelOrderByName: Map<string, number>,
): { points: number[]; ratings: RatingName[]; settledRating: string } | null {
  const tier = rewardTable[rewardIndex];
  if (!tier) return null;

  const levelOrder = levelOrderByName.get(tier.rating) ?? 0;
  const ratedCount = countRatings(levelOrder);

  const matchedRatings = tier.ratings.find(
    (entry) => entry !== null && entry[0] <= numTeams,
  );

  const hasRewards = matchedRatings !== null && matchedRatings !== undefined;
  const hasEnoughRated = ratedCount >= tier.minRated;

  if (
    (!hasRewards || !hasEnoughRated) &&
    (numTeams >= MIN_SANCTION_TEAMS || hasRewards) &&
    (tier.autoSanction === null || numTeams < tier.autoSanction)
  ) {
    const lower = lowerRewardIndex(rewardIndex);
    if (lower === null) return null;
    return getRatedRewards(
      rewardTable,
      lower,
      Math.max(numTeams, MAGIC_RESANCTION_TEAMS),
      countRatings,
      levelOrderByName,
    );
  }

  if (!hasRewards || matchedRatings === null || matchedRatings === undefined) {
    return null;
  }

  return {
    points: [...tier.points],
    ratings: [...matchedRatings[1]],
    settledRating: tier.rating,
  };
}

// ---------------------------------------------------------------------------
// High-level sanctioning check
// ---------------------------------------------------------------------------

export type SanctionResult =
  | { sanctioned: false }
  | { sanctioned: true; sanctionedAs: string };

export type SanctionLevel = "sanctioned" | "downgraded" | "unsanctioned";

/**
 * Classify a sanction result relative to the division it was played in:
 * sanctioned at its own level, sanctioned at a lower level (downgraded),
 * or not sanctioned at all.
 */
export function getSanctionLevel(
  sanction: SanctionResult,
  divisionName: string,
): SanctionLevel {
  if (!sanction.sanctioned) return "unsanctioned";
  return divisionToRewardIndex(sanction.sanctionedAs) ===
    divisionToRewardIndex(divisionName)
    ? "sanctioned"
    : "downgraded";
}

function formatSanctionedAs(name: string): string {
  return match(name)
    .with(P.union("open", "aaa"), () => "Open")
    .with("unrated", () => "Unrated")
    .otherwise((n) => n.toUpperCase());
}

/**
 * Human-readable sanction label, e.g. "Sanctioned", "Sanctioned as B",
 * "Not sanctioned".
 */
export function getSanctionLabel(
  sanction: SanctionResult,
  divisionName: string,
): string {
  return match(getSanctionLevel(sanction, divisionName))
    .with("sanctioned", () => "Sanctioned")
    .with("downgraded", () =>
      sanction.sanctioned
        ? `Sanctioned as ${formatSanctionedAs(sanction.sanctionedAs)}`
        : "Not sanctioned",
    )
    .with("unsanctioned", () => "Not sanctioned")
    .exhaustive();
}

/**
 * Determine the sanctioning status of a tournament division.
 *
 * Returns the level the division is sanctioned at (which may be lower
 * than the division itself if it was downgraded), or `sanctioned: false`.
 */
export function getDivisionSanctionStatus(params: {
  divisionName: string;
  gender: Gender;
  isJuniors: boolean;
  isRec: boolean;
  numTeams: number;
  playerLevelOrders: number[];
  levelOrderByName: Map<string, number>;
  minJuniorsSanctionTeams?: number;
}): SanctionResult {
  if (params.isRec) return { sanctioned: false };
  if (params.isJuniors) {
    const threshold = Math.max(
      1,
      params.minJuniorsSanctionTeams ?? MIN_JUNIORS_SANCTION_TEAMS,
    );
    return params.numTeams >= threshold
      ? { sanctioned: true, sanctionedAs: params.divisionName }
      : { sanctioned: false };
  }
  if (params.gender === "coed") return { sanctioned: false };

  const rewardIndex = divisionToRewardIndex(params.divisionName);
  if (rewardIndex === null) return { sanctioned: false };

  const rewardTable = params.gender === "male" ? MENS_REWARDS : WOMENS_REWARDS;
  const countRatings = (minOrder: number) =>
    params.playerLevelOrders.filter((o) => o >= minOrder).length;

  const rewards = getRatedRewards(
    rewardTable,
    rewardIndex,
    params.numTeams,
    countRatings,
    params.levelOrderByName,
  );

  if (!rewards) return { sanctioned: false };
  return { sanctioned: true, sanctionedAs: rewards.settledRating };
}

/**
 * Query the database and determine whether a tournament division is
 * sanctioned. Counts registered + confirmed teams (for pre-tournament
 * estimates; the finalization system uses confirmed only).
 */
export async function checkDivisionSanctioned(
  dbConn: Database,
  tournamentDivisionId: number,
): Promise<SanctionResult> {
  const [division, allLevels, juniorsSetting] = await Promise.all([
    dbConn.query.tournamentDivisions.findFirst({
      where: { id: tournamentDivisionId },
      columns: { gender: true },
      with: {
        division: { columns: { name: true, maxAge: true } },
        requirements: { columns: { id: true } },
        teams: {
          columns: { status: true },
          with: {
            team: {
              columns: {},
              with: {
                players: {
                  columns: {},
                  with: {
                    profile: { columns: { levelId: true } },
                  },
                },
              },
            },
          },
        },
      },
    }),
    dbConn.query.levels.findMany(),
    dbConn.query.settings.findFirst({
      where: { key: "min-juniors-sanction-teams" },
      columns: { value: true },
    }),
  ]);

  if (!division) return { sanctioned: false };

  const minJuniorsSanctionTeams =
    typeof juniorsSetting?.value === "number"
      ? juniorsSetting.value
      : undefined;

  const activeTeams = division.teams.filter(
    (t) => t.status === "confirmed" || t.status === "registered",
  );

  const levelById = new Map(allLevels.map((l) => [l.id, l]));
  const levelOrderByName = new Map(allLevels.map((l) => [l.name, l.order]));

  const playerLevelOrders: number[] = [];
  for (const tdt of activeTeams) {
    for (const tp of tdt.team.players) {
      if (tp.profile.levelId != null) {
        const level = levelById.get(tp.profile.levelId);
        if (level) playerLevelOrders.push(level.order);
      }
    }
  }

  return getDivisionSanctionStatus({
    divisionName: division.division.name,
    gender: division.gender,
    isJuniors: division.division.maxAge !== null,
    isRec: division.requirements.length > 0,
    numTeams: activeTeams.length,
    playerLevelOrders,
    levelOrderByName,
    minJuniorsSanctionTeams,
  });
}
